import { useTranslation } from "@redotech/redo-i18n";
import { PriceOption } from "@redotech/redo-return-core-common/condition-options";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import type { TotalReturnValueState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { memo, useMemo } from "react";
import { getPriceOptionLabel } from "./price-option-labels";

export const TotalReturnValueDetails = memo(function TotalReturnValueDetails({
  state,
  setState,
  disabled,
  flowType,
}: ConditionTypeDetailsProps<TotalReturnValueState>) {
  const { t } = useTranslation();
  function handlePriceOptionChange(priceOption: PriceOption) {
    setState({
      ...state,
      priceOption,
      ...(priceOption === PriceOption.CENTS_EQUALS &&
        +state.amount >= 1 && { amount: "0.99" }),
    });
  }

  function handleAmountChange(amount: string) {
    setState({ ...state, amount });
  }

  const dropdownOptionsValues =
    flowType === FlowType.FINALIZE_RETURN ||
    flowType === FlowType.FINALIZE_CLAIM
      ? Object.values(PriceOption).filter(
          (option) => option !== PriceOption.CENTS_EQUALS,
        )
      : Object.values(PriceOption);

  const priceOptions: RedoListItem<PriceOption>[] = useMemo(
    () =>
      dropdownOptionsValues.map((option) => ({
        id: option,
        value: option,
        type: "text" as const,
        text: getPriceOptionLabel(option, t),
      })),
    [dropdownOptionsValues, t],
  );

  const selectedOption = state.priceOption
    ? priceOptions.find((option) => option.value === state.priceOption)
    : undefined;

  return (
    <>
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        flexGrow
        label={t("Comparison")}
        options={priceOptions}
        optionSelected={({ item }) => handlePriceOptionChange(item.value)}
        placeholder={t("Select comparison...")}
        selectedItem={selectedOption}
        size="xs"
      />
      {state.priceOption === PriceOption.CENTS_EQUALS ? (
        <RedoTextInput
          IconLeading={() => <>$</>}
          inputTypeHint="number"
          max={0.99}
          min={0}
          onBlur={() => handleAmountChange((+state.amount).toFixed(2))}
          setValue={(e) => handleAmountChange(e)}
          state={disabled ? "readonly" : "default"}
          step={0.01}
          value={state.amount}
        />
      ) : (
        <RedoTextInput
          IconLeading={() => <>$</>}
          inputTypeHint="number"
          min={0}
          onBlur={() => handleAmountChange((+state.amount).toFixed(2))}
          setValue={(e) => handleAmountChange(e)}
          state={disabled ? "readonly" : "default"}
          value={state.amount}
        />
      )}
    </>
  );
});
